import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, Search, Check } from 'lucide-react';

/**
 * Premium searchable multi-select with removable chips + keyboard navigation.
 * Props:
 *  - options: [{ value, label, disabled? }]
 *  - value: array of selected values (order preserved by parent)
 *  - onToggle(value): called when an option is added/removed
 *  - onRemove(value): called when a chip's × is clicked
 *  - placeholder
 *  - testId
 */
const SearchableMultiSelect = ({
  options = [],
  value = [],
  onToggle,
  onRemove,
  placeholder = 'Select…',
  testId = 'multi-select',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const labelOf = (v) => (options.find((o) => o.value === v) || {}).label || v;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => !q || o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => { setActiveIndex(0); }, [query, open]);

  const openMenu = () => { setOpen(true); inputRef.current?.focus(); };

  const commit = (opt) => {
    if (!opt || opt.disabled) return;
    onToggle(opt.value);
    setQuery('');
    setActiveIndex(0);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered[activeIndex]) commit(filtered[activeIndex]);
      else setOpen(true);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Backspace' && query === '' && value.length > 0) {
      onRemove(value[value.length - 1]);
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div
        className={`w-full bg-slate-800/70 border rounded-xl px-2.5 py-2 flex flex-wrap items-center gap-2 cursor-text transition-colors ${open ? 'border-blue-500' : 'border-slate-700'}`}
        onClick={openMenu}
      >
        <AnimatePresence initial={false}>
          {value.map((v) => (
            <motion.span
              key={v}
              layout
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15 }}
              className="inline-flex items-center gap-1.5 bg-blue-500/15 border border-blue-500/30 text-blue-300 rounded-full pl-3 pr-1.5 py-1 text-xs font-medium"
              data-testid={`${testId}-chip-${v}`}
            >
              {labelOf(v)}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(v); }}
                className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-blue-500/30 transition-colors"
                aria-label={`Remove ${labelOf(v)}`}
                data-testid={`${testId}-chip-remove-${v}`}
              >
                <X className="w-3 h-3" aria-hidden="true" />
              </button>
            </motion.span>
          ))}
        </AnimatePresence>

        <div className="flex-1 min-w-[80px] flex items-center gap-1">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onKeyDown={handleKeyDown}
            onFocus={() => setOpen(true)}
            placeholder={value.length === 0 ? placeholder : 'Add more…'}
            className="w-full bg-transparent text-white text-sm placeholder-slate-500 focus:outline-none py-1"
            data-testid={`${testId}-input`}
            role="combobox"
            aria-expanded={open}
            aria-controls={`${testId}-listbox`}
          />
        </div>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180 text-blue-400' : ''}`}
          aria-hidden="true"
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute z-30 mt-2 w-full bg-slate-800 border border-slate-700 rounded-xl shadow-2xl shadow-black/40 overflow-hidden"
            id={`${testId}-listbox`}
            role="listbox"
          >
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/70">
              <Search className="w-3.5 h-3.5 text-slate-500" aria-hidden="true" />
              <span className="text-xs text-slate-500">Type to search relationships</span>
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <p className="px-3 py-3 text-sm text-slate-500" data-testid={`${testId}-empty`}>No matches</p>
              )}
              {filtered.map((o, idx) => {
                const isSelected = value.includes(o.value);
                const isActive = idx === activeIndex;
                return (
                  <button
                    key={o.value}
                    type="button"
                    disabled={o.disabled}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => commit(o)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                      o.disabled
                        ? 'text-slate-600 cursor-not-allowed'
                        : isActive
                        ? 'bg-slate-700/70 text-white'
                        : 'text-slate-200 hover:bg-slate-700/50'
                    }`}
                    data-testid={`${testId}-option-${o.value}`}
                    aria-selected={isSelected}
                    role="option"
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-600'}`}>
                      {isSelected && <Check className="w-3 h-3 text-white" aria-hidden="true" />}
                    </span>
                    <span>{o.label}</span>
                    {o.disabled && <span className="ml-auto text-[10px] text-slate-600">Unavailable</span>}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchableMultiSelect;
